import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import { UserProvider, useUser } from "./context/UserContext";
import MainLayout from "./layout/MainLayout";
import CreateSignature from "./component/pages/CreateSignature";
import AddressMaster from "./component/pages/AddressMaster";
import UserMaster from "./component/pages/UserMaster";
import RoleMaster from "./component/pages/RoleMaster";
import AuditLog from "./component/pages/AuditLog";
import Login from "./component/login/Login";
import InactiveEmployee from "./component/auth/InactiveEmployee";
import { fetchApi } from "./api";
import { fetchHrisEmployeeData } from "./hrisApi";
import kmsClientEncryption from "./utils/kmsClientEncryption";
 
const msalInstance = new PublicClientApplication(msalConfig);
 
const msalReady = msalInstance.initialize().then(() =>
  msalInstance.handleRedirectPromise().then(async (response) => {
    if (response) {
      const account = response.account;
      const email = account?.username || account?.name || "";
      const displayName = account?.name || "";
      
      if (email) sessionStorage.setItem("userEmail", email);
      if (displayName) sessionStorage.setItem("userDisplayName", displayName);

      // Call backend login to get session token
      if (email) {
        try {
          // const BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, ""); // Sonar fix: ReDoS vulnerable regex replaced
          const BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
          const resp = await fetch(`${BASE}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ emailId: email, userName: displayName })
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.token) sessionStorage.setItem("gs_session_token", data.token);
          }
        } catch (e) { /* login call failed */ }
      }
      
      window.location.replace("/app/CreateSignature");
    }
  })
).catch((err) => { /* GS-020: console.error("MSAL redirect error:", err) */ });
 
const AccessDenied = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', fontFamily: 'GB-BodyCopy' }}>
    <h2 style={{ color: '#810055' }}>Access Denied</h2>
    <p style={{ color: '#707070', marginTop: '10px' }}>You are not authorized to access this application.</p>
  </div>
);

const InactiveUser = () => {
  const message = sessionStorage.getItem("inactiveMessage") || "Your account has been deactivated. Please contact HR for assistance.";
  const reason = sessionStorage.getItem("inactiveReason") || 'unknown';
  
  // Enhanced message based on reason
  const getEnhancedMessage = () => {
    const baseMessage = message;
    switch(reason) {
      case 'severance':
        return `${baseMessage} (Employment Termination)`;
      case 'user_master':
        return `${baseMessage} (System Administration)`;
      default:
        return baseMessage;
    }
  };
  
  // Clear inactive status from sessionStorage when component unmounts
  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem("userInactive");
      sessionStorage.removeItem("inactiveMessage");
      sessionStorage.removeItem("inactiveReason");
    };
  }, []);
  
  return <InactiveEmployee message={getEnhancedMessage()} />;
};

const RoleLoader = () => {
  const { accounts } = useMsal();
  const { role, setRole, setSessionAccess, setDivisionAuthorized, divisionAuthorized } = useUser();

  const createNewUser = async (email, displayName, hrisData) => {
    try {
      const newUser = {
        um_user_id: email.split('@')[0],
        um_email: email,
        um_user_name: displayName || email.split('@')[0],
        um_salary_code: hrisData?.SalaryCode || hrisData?.SalCode || email.split('@')[0],
        um_mobile_no: hrisData?.MobileNo || '',
        um_employee_band: hrisData?.Band || '',
        um_designation: hrisData?.DesignationCode || hrisData?.Designation_Code || '',
        um_department: hrisData?.DepartmentName || hrisData?.Department_Name || '',
        um_role_name: 'user',
        um_status: 'Active',
        um_inserted_by: email,
        um_inserted_on: new Date().toISOString()
      };
      
      // GS-020: Console logs commented out for production
      // console.log("[RoleLoader] Creating new user:", newUser);
      
      // Encrypt PII fields (mobile & telephone) before sending
      const encryptedUser = await kmsClientEncryption.encryptPIIFields(newUser, ["um_mobile_no", "um_telephone_no"]);
      
      await fetchApi("/api/usermaster/save", {
        method: "POST",
        body: JSON.stringify(encryptedUser)
      });
      
      // GS-020: Console logs commented out for production
      // console.log("[RoleLoader] New user created successfully:", email);
      return newUser;
    } catch (err) {
      // GS-020: Console logs commented out for production
      // console.error("[RoleLoader] Failed to create new user:", err);
      return {
        um_email: email,
        um_user_name: displayName || email.split('@')[0],
        um_role_name: 'user',
        um_status: 'Active'
      };
    }
  };

  const loadSessionAccess = async (email) => {
    try {
      // Get user's role name from user master
      const users = await fetchApi("/api/usermaster/all");
      const userArray = Array.isArray(users) ? users : users.users || users.data || [];
      let currentUser = userArray.find(u => u.um_email?.toLowerCase() === email.toLowerCase());
      
      // If user doesn't exist, create new user with HRIS data
      if (!currentUser) {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] New user detected, creating user record for:", email);
        try {
          const hrisData = await fetchHrisEmployeeData(email);
          const displayName = sessionStorage.getItem("userDisplayName") || "";
          currentUser = await createNewUser(email, displayName, hrisData);
          // GS-020: Console logs commented out for production
          // console.log("[RoleLoader] New user created with HRIS data:", currentUser);
        } catch (hrisErr) {
          // GS-020: Console logs commented out for production
          // console.warn("[RoleLoader] HRIS data unavailable for new user, creating with basic info:", hrisErr.message);
          const displayName = sessionStorage.getItem("userDisplayName") || "";
          currentUser = await createNewUser(email, displayName, null);
          // GS-020: Console logs commented out for production
          // console.log("[RoleLoader] New user created with basic info:", currentUser);
        }
        
        // Set default session access for new users (only Generate email signature)
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] Setting default session access for new user");
        setSessionAccess({
          "Generate email signature": true,
          "Master": false,
          "Address Master": false,
          "Admin": false,
          "Role & Authorization": false,
          "User Master": false,
          "Audit Log": false,
        });
        return;
      }
      
      // Check if user is inactive - deny access completely
      if (currentUser.um_status?.toLowerCase() === 'inactive') {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] User is inactive, denying access:", email);
        setSessionAccess({
          "Generate email signature": false,
          "Master": false,
          "Address Master": false,
          "Admin": false,
          "Role & Authorization": false,
          "User Master": false,
          "Audit Log": false,
        });
        setRole("inactive_user");
        return;
      }
      
      if (!currentUser?.um_role_name) {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] User found but no role assigned, setting default access:", email);
        // Set default session access if no role found
        setSessionAccess({
          "Generate email signature": true,
          "Master": false,
          "Address Master": false,
          "Admin": false,
          "Role & Authorization": false,
          "User Master": false,
          "Audit Log": false,
        });
        return;
      }

      // GS-020: Console logs commented out for production
      // console.log("[RoleLoader] Existing user found with role:", currentUser.um_role_name, "for email:", email);

      // Get role's session access from role master
      const roles = await fetchApi("/api/rolemaster/all");
      const roleArray = Array.isArray(roles) ? roles : roles.roles || roles.data || [];
      const matchedRole = roleArray.find(r => r.rm_role?.toLowerCase() === currentUser.um_role_name.toLowerCase());
      
      // Check if role is inactive - restrict to only Generate email signature
      if (matchedRole && matchedRole.rm_status?.toLowerCase() === 'inactive') {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] User's role is inactive, restricting to signature generation only:", currentUser.um_role_name);
        setSessionAccess({
          "Generate email signature": true,
          "Master": false,
          "Address Master": false,
          "Admin": false,
          "Role & Authorization": false,
          "User Master": false,
          "Audit Log": false,
        });
        return;
      }
      
      if (matchedRole?.rm_session_access) {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] Setting session access from role:", matchedRole.rm_role, matchedRole.rm_session_access);
        // Convert array format from DB to object format for context
        if (Array.isArray(matchedRole.rm_session_access)) {
          const accessObj = {
            "Generate email signature": false,
            "Master": false,
            "Address Master": false,
            "Admin": false,
            "Role & Authorization": false,
            "User Master": false,
            "Audit Log": false,
          };
          matchedRole.rm_session_access.forEach(name => { if (accessObj.hasOwnProperty(name)) accessObj[name] = true; });
          setSessionAccess(accessObj);
        } else {
          setSessionAccess(matchedRole.rm_session_access);
        }
      } else {
        // GS-020: Console logs commented out for production
        // console.log("[RoleLoader] No role match found, setting default session access for:", currentUser.um_role_name);
        // Set default session access if no role match found
        setSessionAccess({
          "Generate email signature": true,
          "Master": false,
          "Address Master": false,
          "Admin": false,
          "Role & Authorization": false,
          "User Master": false,
          "Audit Log": false,
        });
      }
    } catch (err) {
      // GS-020: Console logs commented out for production
      // console.error("[RoleLoader] Failed to load session access:", err);
      // console.log("[RoleLoader] Setting default session access due to error for:", email);
      // Set default session access on error
      setSessionAccess({
        "Generate email signature": true,
        "Master": false,
        "Address Master": false,
        "Admin": false,
        "Role & Authorization": false,
        "User Master": false,
        "Audit Log": false,
      });
    }
  };
 
  const validateDivision = async (email) => {
    try {
      const hrisData = await fetchHrisEmployeeData(email);
      const companyCode = hrisData?.Company || hrisData?.rawData?.Company_Code || '';
      // GS-020: Console logs commented out for production
      // console.log(`[RoleLoader] User division/company: ${companyCode} - Access granted for both GIL and G&B users`);
      setDivisionAuthorized(true);
      return true;
    } catch (err) {
      // GS-020: Console logs commented out for production
      // console.error('[RoleLoader] HRIS division check failed:', err.message);
      // If HRIS is unreachable, still allow access
      // console.log('[RoleLoader] HRIS unavailable, allowing access for all users');
      setDivisionAuthorized(true);
      return true;
    }
  };

  useEffect(() => {
    // GS-SEC-019: Localhost auto-admin bypass commented out for security
    // This block granted full admin access when running on localhost.
    // For local development, use MSAL login flow same as production.
    // if (window.location.hostname === "localhost") {
    //   setRole("admin");
    //   setDivisionAuthorized(true);
    //   setSessionAccess({
    //     "Generate email signature": true,
    //     "Master": true,
    //     "Address Master": true,
    //     "Admin": true,
    //     "Role & Authorization": true,
    //     "User Master": true,
    //     "Audit Log": true,
    //   });
    //   if (!localStorage.getItem("userEmail")) {
    //     fetchApi("/api/auth/user")
    //       .then((data) => {
    //         const email = data?.email || data?.username || "";
    //         const name = data?.name || data?.displayName || "";
    //         if (email) localStorage.setItem("userEmail", email);
    //         if (name) localStorage.setItem("userDisplayName", name);
    //       })
    //       .catch(() => {});
    //   }
    //   return;
    // }
    if (accounts.length > 0) {
      const account = accounts[0];
      const email = account?.username || account?.name || "";
      const displayName = account?.name || "";
      // GS-020: Console logs commented out for production
      // console.log("[RoleLoader] AD account:", JSON.stringify({
      //   username: account?.username,
      //   name: account?.name,
      //   idTokenClaims: account?.idTokenClaims
      // }));
      if (email) sessionStorage.setItem("userEmail", email);
      if (displayName) sessionStorage.setItem("userDisplayName", displayName);

      // Load user role and session access for both GIL and G&B users
      if (divisionAuthorized === null) {
        validateDivision(email).then(() => {
          if (!role) {
            // GS-SEC-008: Hardcoded admin user list commented out for security
            // Admin access should be managed via UserMaster + RoleMaster tables in the backend.
            // Ensure these users have proper admin roles assigned in the database.
            // const adminUsers = ["rautsh", "ashwini", "priyanka", "himali", "shailaja", "vinayab"];
            // const isAdmin = adminUsers.some(u => email.toLowerCase().includes(u.toLowerCase()));
            // if (isAdmin) {
            //   setRole("admin");
            //   if (email.toLowerCase().includes("rautsh")) {
            //     setSessionAccess({
            //       "Generate email signature": true,
            //       "Master": true,
            //       "Address Master": true,
            //       "Admin": true,
            //       "Role & Authorization": true,
            //       "User Master": true,
            //       "Audit Log": true,
            //     });
            //   } else {
            //     loadSessionAccess(email);
            //   }
            // } else {
            //   setRole("user");
            //   loadSessionAccess(email);
            // }
            
            // All users now go through backend role lookup
            loadSessionAccess(email).then(() => {
              if (!role) setRole("user");
            });
          }
        });
      }
    }
  }, [accounts, role, setRole, divisionAuthorized]);
 
  return null;
};
 
const ProtectedRoute = ({ element, adminOnly, sessionKey }) => {
  const { role, sessionAccess, divisionAuthorized } = useUser();
  if (divisionAuthorized === false) return <AccessDenied />;
  if (role === "inactive_user") return <InactiveUser />;
  if (role === null || sessionAccess === null) return null;
  
  // Check session access for the specific feature
  if (sessionAccess && sessionKey && !sessionAccess[sessionKey]) {
    return <Navigate to="/app/CreateSignature" replace />;
  }
  
  // For admin-only routes, check if user has admin role or specific session access
  if (adminOnly && role !== "admin" && (!sessionAccess || !sessionAccess[sessionKey])) {
    return <Navigate to="/app/CreateSignature" replace />;
  }
  
  return element;
};
 
const DivisionGate = ({ children }) => {
  const { divisionAuthorized, sessionAccess, role } = useUser();
  if (divisionAuthorized === false) return <AccessDenied />;
  if (role === "inactive_user") return <InactiveUser />;
  if (divisionAuthorized === null || sessionAccess === null) return null;
  return children;
};

function App() {
  const [ready, setReady] = useState(false);
 
  useEffect(() => {
    msalReady.then(() => setReady(true)).catch(() => setReady(true));
  }, []);
 
  if (!ready) return null;
 
  return (
<MsalProvider instance={msalInstance}>
<UserProvider>
<Router>
<RoleLoader />
<Routes>
<Route path="/" element={<Login />} />
<Route path="/login" element={<Login />} />
<Route path="/inactive" element={<InactiveUser />} />
<Route path="/auth" element={<Navigate to="/app/CreateSignature" replace />} />
 
            <Route path="/app" element={<MainLayout />}>
              <Route index element={<Navigate to="CreateSignature" replace />} />
              <Route path="CreateSignature" element={<DivisionGate><CreateSignature /></DivisionGate>} />
              <Route path="AddressMaster" element={<ProtectedRoute element={<AddressMaster />} adminOnly sessionKey="Address Master" />} />
              <Route path="UserMaster" element={<ProtectedRoute element={<UserMaster />} adminOnly sessionKey="User Master" />} />
              <Route path="RoleMaster" element={<ProtectedRoute element={<RoleMaster />} adminOnly sessionKey="Role & Authorization" />} />
              <Route path="AuditLog" element={<ProtectedRoute element={<AuditLog />} adminOnly sessionKey="Audit Log" />} />
            </Route>
</Routes>
</Router>
</UserProvider>
</MsalProvider>
  );
}
 
export default App;